import { useState } from 'react';

import type { OutcomeFollowUpAction } from '@modules/_shared/components/feed/activityOutcomeShared';
import type {
  ActivityType,
  ListingActivityContext,
  PendingPlanActivity,
} from '@modules/_shared/components/feed/types';

// Activity types that can be planned/conflicted (excludes 'note')
type PlanActivityType = Extract<ActivityType, 'call' | 'visit' | 'task'>;

export type { PendingPlanActivity };

type ConflictState = {
  action: PlanActivityType;
  task: PendingPlanActivity;
  context?: ListingActivityContext | null;
} | null;

type PendingActivitiesLookup = Partial<
  Record<PlanActivityType, PendingPlanActivity[]>
>;

type UsePlanActivityConflictParams = {
  pendingActivitiesByType?: PendingActivitiesLookup;
  onPlanActivitySelect?: (
    action: PlanActivityType,
    context?: ListingActivityContext | null,
  ) => void;
};

export const usePlanActivityConflict = ({
  pendingActivitiesByType,
  onPlanActivitySelect,
}: UsePlanActivityConflictParams) => {
  const [pendingConflict, setPendingConflict] = useState<ConflictState>(null);
  const [callConflict, setCallConflict] = useState<PendingPlanActivity | null>(
    null,
  );
  const [visitConflict, setVisitConflict] =
    useState<PendingPlanActivity | null>(null);
  const [taskConflict, setTaskConflict] = useState<PendingPlanActivity | null>(
    null,
  );

  const pendingActivitiesLookup = pendingActivitiesByType ?? {};

  const handlePlanActivityClick = (action: PlanActivityType) => {
    const pendingTasks = pendingActivitiesLookup[action];
    const singlePendingTask =
      pendingTasks?.length === 1 ? pendingTasks[0] : null;

    if (singlePendingTask) {
      setPendingConflict({ action, task: singlePendingTask });
      return;
    }

    onPlanActivitySelect?.(action);
  };

  const handleConflictClose = () => {
    setPendingConflict(null);
  };

  const handleConfirmCreateNew = () => {
    if (pendingConflict) {
      onPlanActivitySelect?.(pendingConflict.action, pendingConflict.context);
    }

    // Don't immediately close - let the dialog animate out first
    // handleConflictClose will be called by onClose prop
  };

  const handleConfirmUpdateExisting = () => {
    if (!pendingConflict) {
      return;
    }

    const { action, task } = pendingConflict;

    if (action === 'call') {
      setCallConflict(task);
      // Don't immediately close - let the dialog animate out first
      return;
    }

    if (action === 'visit') {
      setVisitConflict(task);
      // Don't immediately close - let the dialog animate out first
      return;
    }

    if (action === 'task') {
      setTaskConflict(task);
      // Don't immediately close - let the dialog animate out first
      return;
    }
  };

  const handleOutcomeFollowUpAction = (action: OutcomeFollowUpAction) => {
    switch (action) {
      case 'schedule_visit':
        handlePlanActivityClick('visit');
        return;
      case 'schedule_call':
        handlePlanActivityClick('call');
        return;
      case 'schedule_task':
        handlePlanActivityClick('task');
        return;
      default:
        return;
    }
  };

  return {
    pendingConflict,
    callConflict,
    visitConflict,
    taskConflict,
    handlePlanActivityClick,
    handleConflictClose,
    handleConfirmCreateNew,
    handleConfirmUpdateExisting,
    handleOutcomeFollowUpAction,
    setPendingConflict,
    setCallConflict,
    setVisitConflict,
    setTaskConflict,
  };
};
